import React from "react";
import {
  Cpu,
  Zap,
  TrendingUp,
  Boxes,
  Shield,
  Clock,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Workflow,
  FileSearch,
  Users,
  Building2,
  BarChart3,
  CheckCircle2,
  Database
} from "lucide-react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../Styling/AT.css";
import Navbar from "../Components/Navbar";
import Call from "../Components/Call";
import Footer from "../Components/Footer";
import ChatbotWidget from "./ChatComponent";
import TrustStatement from "../Components/TrustStatement";

gsap.registerPlugin(ScrollTrigger);

const containerVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0,
      delayChildren: 0
    }
  }
};

const itemVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0 }
  }
};

export default function AT() {
  const [currentFeature, setCurrentFeature] = React.useState(0);
  const totalFeatures = 3;

  const mainRef = React.useRef(null);
  const featuresRef = React.useRef(null);
  const capabilitiesRef = React.useRef(null);
  const engagementRef = React.useRef(null);

  // Auto slide
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % totalFeatures);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const ctx = gsap.context((self) => {
      // Hero Visual Entrance - instant
      gsap.set(".AT-hero-visual > *", {
        opacity: 1,
        y: 0,
        scale: 1
      });

      // Background Blobs
      gsap.to(".AT-blob", {
        y: "30%",
        rotation: 10,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: {
          each: 2,
          from: "random"
        }
      });

      // Mesh Overlay Rotation
      gsap.to(".AT-mesh-overlay", {
        rotation: 360,
        duration: 120,
        repeat: -1,
        ease: "linear"
      });

      // Show all sections instantly
      gsap.set(".AT-section-header, .AT-capability-card, .AI-Target-Entity-Card, .AT-step", {
        opacity: 1,
        y: 0,
        scale: 1
      });

      ScrollTrigger.refresh();
    }, mainRef);

    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);

    return () => {
      ctx.revert();
      clearTimeout(refreshTimer);
    };
  }, []);

  const nextFeature = () => {
    setCurrentFeature((prev) => (prev + 1) % totalFeatures);
  };

  const prevFeature = () => {
    setCurrentFeature((prev) => (prev === 0 ? totalFeatures - 1 : prev - 1));
  };

  const goToFeature = (index) => {
    setCurrentFeature(index);
  };

  return (
    <div className="AT-page-wrapper" ref={mainRef}>
      <Navbar />

      {/* HERO SECTION (CINEMATIC) */}
      <section className="AT-header-section">
        {/* Background Effects */}
        <div className="AT-mesh-container">
          <div className="AT-mesh-overlay"></div>
        </div>
        <div className="AT-blob blob-purple"></div>
        <div className="AT-blob blob-blue"></div>
        <div className="AT-blob blob-cyan"></div>

        <div className="container">
          <div className="AT-hero-layout">
            <motion.div
              className="AT-hero-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div className="AT-header-badge" variants={itemVariants}>
                <Cpu size={16} />
                <span>Autonomous Operations</span>
              </motion.div>

              <motion.h1 className="AT-header-title" variants={itemVariants}>
                AI Systems That Replace <span className="blue-gradient">Manual Operations</span>
              </motion.h1>

              <motion.p className="AT-header-desc" variants={itemVariants}>
                We design and deploy AI automation systems that eliminate repetitive workflows, fragmented tools, and operational bottlenecks. Built for real environments, typically achieving <span style={{ color: "white", fontWeight: "700" }}>40-60% reduction</span> in operational overhead.
              </motion.p>

              <motion.div className="AT-hero-ctas" variants={itemVariants}>
                <a
                  href="https://studio.digitaldreamworksagency.com/access"
                  className="AT-btn-hero"
                  onClick={() => {
                    if (window.fbq) {
                      window.fbq('trackCustom', 'RequestDemoOpened', {
                        source: 'at_hero',
                        page_title: 'Autonomous Systems (AT)'
                      });
                    }
                  }}
                >
                  Request Demo Access <ArrowRight size={18} />
                </a>
                <div className="AT-trust">
                  <CheckCircle2 size={16} color="#3b82f6" />
                  <span>Enterprise Ready</span>
                </div>
              </motion.div>
            </motion.div>

            <div className="AT-hero-visual">
              <div className="visual-card main-card">
                <Workflow size={64} strokeWidth={1.5} />
                <span>Workflow AI</span>
              </div>
              <div className="visual-card secondary-card card-1">
                <Database size={32} color="#3b82f6" />
                <span>Unified Data</span>
              </div>
              <div className="visual-card secondary-card card-2">
                <Zap size={32} color="#8b5cf6" />
                <span>Zero Latency</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="AI-System-Slider-Section" ref={featuresRef}>
        <div className="container">
          <div className="AI-System-Architecture-Header">
            <h2 className="AI-Infrastructure-Title">System Architecture</h2>
            <p className="AI-Infrastructure-Subtitle">Advanced AI layers engineered for specific operational roles.</p>
          </div>

          <div className="AI-System-Carousel">
            <div className="AI-Carousel-Viewport">
              {[
                {
                  title: "Operations Intelligence",
                  desc: "Real‑time decision‑making AI for complex internal workflows and operational routing.",
                  tag: "Smart Logic Layer",
                  icon: <Zap size={44} />
                },
                {
                  title: "Autonomous Execution",
                  desc: "AI agents that act, route, and escalate tasks without human coordination overhead.",
                  tag: "Zero-Human Intervention",
                  icon: <Workflow size={44} />
                },
                {
                  title: "Scalable Automation",
                  desc: "Systems designed to grow with volume, teams, and operational complexity.",
                  tag: "Enterprise Scale",
                  icon: <TrendingUp size={44} />
                }
              ].map((slide, index) => (
                <div
                  key={index}
                  className={`AI-System-Slide ${currentFeature === index ? "is-active" : ""}`}
                >
                  <div className="AI-Slide-Inner">
                    <div className="AI-Slide-Icon">{slide.icon}</div>
                    <h3 className="AI-Slide-Heading">{slide.title}</h3>
                    <p className="AI-Slide-Text">{slide.desc}</p>
                    <div className="AI-Slide-Meta">{slide.tag}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="AI-Carousel-Navigation">
              <button className="AI-Nav-Arrow prev" onClick={prevFeature} aria-label="Previous slide">
                <ChevronLeft size={28} />
              </button>

              <div className="AI-Nav-Pagination">
                {[0, 1, 2].map(i => (
                  <button
                    key={i}
                    className={`AI-Page-Dot ${currentFeature === i ? "is-active" : ""}`}
                    onClick={() => goToFeature(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>

              <button className="AI-Nav-Arrow next" onClick={nextFeature} aria-label="Next slide">
                <ChevronRight size={28} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT THIS SYSTEM SOLVES */}
      <section className="AI-Infrastructure-Problems-Section">
        <div className="container">
          <div className="AI-Problems-Banner-Box">
            <div className="AI-Problems-Text-Side">
              <h4 className="AI-Problems-Heading">What This System Solves?</h4>
              <p className="AI-Problems-Lead-Text">Eliminating the frictions that slow down scaling.</p>
            </div>
            <div className="AI-Problems-Checklist-Grid">
              {[
                { icon: <Users size={18} />, text: "Manual operational teams" },
                { icon: <Clock size={18} />, text: "Slow approval cycles" },
                { icon: <Boxes size={18} />, text: "Disconnected tools & spreadsheets" },
                { icon: <Shield size={18} />, text: "Human‑dependent execution" }
              ].map((item, i) => (
                <div key={i} className="AI-Problem-Check-Item">
                  <div className="AI-Problem-Check-Icon"><Zap size={18} color="#4ade80" /></div>
                  <span className="AI-Problem-Check-Label">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD (Capabilities) */}
      <section className="AT-capabilities-section" ref={capabilitiesRef}>
        <div className="container">
          <div className="AT-section-header centered">
            <span className="AT-section-badge">Capabilities</span>
            <h2 className="AT-section-title">What We Build</h2>
          </div>

          <div className="AT-capabilities-grid">
            <div className="AT-capability-card">
              <div className="card-top">
                <div className="AT-capability-icon blue">
                  <Workflow color="white" size={24} />
                </div>
                <h3 className="AT-capability-title">AI Agents</h3>
              </div>
              <p className="AT-capability-desc">
                AI agents for internal operations and support that execute tasks autonomously.
              </p>
            </div>

            <div className="AT-capability-card">
              <div className="card-top">
                <div className="AT-capability-icon purple">
                  <Zap color="white" size={24} />
                </div>
                <h3 className="AT-capability-title">Orchestration Engines</h3>
              </div>
              <p className="AT-capability-desc">
                Workflow orchestration engines that synchronize complex data and team movements.
              </p>
            </div>

            <div className="AT-capability-card">
              <div className="card-top">
                <div className="AT-capability-icon indigo">
                  <FileSearch color="white" size={24} />
                </div>
                <h3 className="AT-capability-title">Document Intelligence</h3>
              </div>
              <p className="AT-capability-desc">
                Document processing and classification systems that turn raw data into action.
              </p>
            </div>

            <div className="AT-capability-card">
              <div className="card-top">
                <div className="AT-capability-icon blue">
                  <Cpu color="white" size={24} />
                </div>
                <h3 className="AT-capability-title">Custom AI Tools</h3>
              </div>
              <p className="AT-capability-desc">
                Custom internal AI tools precisely aligned to your unique business logic.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* IDEAL FOR SECTION (New) */}
      <section className="AI-Deployment-Targets-Section">
        <div className="container">
          <div className="AI-Targets-Branding-Header">
            <h2 className="AI-Targets-Main-Title">Ideal For</h2>
            <p className="AI-Targets-Context">Who benefits most from autonomous AI systems.</p>
          </div>
          <div className="AI-Targets-Layout-Grid">
            {[
              {
                title: "Agencies",
                desc: "Agencies scaling operations without linear overhead increase.",
                icon: <BarChart3 className="AI-Target-Icon-Svg" />
              },
              {
                title: "Enterprises",
                desc: "Enterprises replacing aging internal ops teams with AI speed.",
                icon: <Building2 className="AI-Target-Icon-Svg" />
              },
              {
                title: "Operators",
                desc: "Operators managing high‑volume, complex business workflows.",
                icon: <Users className="AI-Target-Icon-Svg" />
              }
            ].map((item, i) => (
              <div className="AI-Target-Entity-Card" key={i}>
                <div className="AI-Target-Icon-Container">{item.icon}</div>
                <h4 className="AI-Target-Card-Header">{item.title}</h4>
                <p className="AI-Target-Card-Subtext">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODEL */}
      <section className="AT-engagement-section" ref={engagementRef}>
        <div className="container">
          <div className="AT-section-header centered">
            <h2 className="AT-engagement-heading">Engagement Model</h2>
            <p className="AT-section-desc">A controlled and secure path to operational deployment.</p>
          </div>

          <div className="AT-steps-container">
            {[
              { title: "Evaluation", desc: "Rigorous security and data scope assessment." },
              { title: "Demo", desc: "Experience the system logic with your data." },
              { title: "System Design", desc: "Custom design for your specific workflows." },
              { title: "Licensing", desc: "Paid deployment and commercial licensing." }
            ].map((step, i) => (
              <div className="AT-step" key={i}>
                <div className="step-number-container">
                  <div className="step-number">{i + 1}</div>
                  {i < 3 && <div className="step-connector"></div>}
                </div>
                <div className="step-content">
                  <h4 className="step-title">{step.title}</h4>
                  <p className="step-desc">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrustStatement />

      <div className="LD-divider"></div>
      <ChatbotWidget />
      <Call />
      <Footer />
    </div>
  );
}
